import { FC, useEffect, useMemo } from 'react'
import { useFormik } from 'formik'
import {
  AtendimentoType,
  ButtonType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { profissionalValorSchema } from './validation/profissionalValorSchema'
import { getTiposAtendimentosByName } from '../../../../../../../_metronic/helpers/crud-helper/_request'
import { ValorProfissionalDtoCadastrar, ValorProfissionalView } from '../../core/_models'
import { createValoresByProfissional } from '../../core/_requests'
import { confirmarReprocessamentoFaturas } from '../../../funcionarios-list/core/_reprocessamentoFaturas'
import { useT } from '_metronic/i18n/Metronici18n'

type IProps = { idProfissional: number }

export const ProfissionalValoresModal: FC<IProps> = ({ idProfissional }) => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: valorProfissional } = (fetchStates['valorProfissional'] ||
    {}) as FetchState<ValorProfissionalView>

  const initialValues = {
    tipoAtendimento: undefined as IOption<AtendimentoType> | undefined,
    especialidades: valorProfissional?.especialidade
      ? [{ label: valorProfissional.especialidade.nome, value: valorProfissional.especialidade.id }]
      : ([] as IOption<number>[]),
    valor: valorProfissional?.valor,
    valorRetorno: valorProfissional?.valorRetorno,
    minimo: valorProfissional?.faturamentoMinimo,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => profissionalValorSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      const especialidades =
        values.especialidades.length > 0
          ? values.especialidades.map((especialidade) => especialidade.value)
          : null

      try {
        const data: ValorProfissionalDtoCadastrar = {
          tipo: values.tipoAtendimento?.value as AtendimentoType,
          especialidades,
          valor: Number(values.valor),
          valorRetorno: values.valorRetorno,
          minimo: values.minimo || null,
        }
        // Valor novo ou alterado, tanto faz: a rota já exige Financeiro:write, então a pergunta vale sempre.
        const reprocessarFaturas = await confirmarReprocessamentoFaturas()
        await createValoresByProfissional(idProfissional, data, reprocessarFaturas)
        await showAlert({
          title: 'Sucesso!',
          text: `Valor ${valorProfissional ? 'atualizado' : 'cadastrado'} com sucesso!`,
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { setFieldValue } = formik
  const tipo = valorProfissional?.tipo

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (tipo) {
          const data = await getTiposAtendimentosByName(tipo)
          setFieldValue('tipoAtendimento', { label: data.descricao, value: data.name })
        }
      } catch (error) {
        showError(error)
      }
    }

    fetchData()
  }, [tipo, setFieldValue])

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className='row'>
        <FormSelect
          formik={formik}
          name='especialidades'
          entity='especialidade'
          placeholder='Selecione especialidades (ou deixe vazio para todas)'
          fetchOptions={OptionsHelper.getOptionsEspecialidades}
          isDisabled={formik.isSubmitting || !!valorProfissional}
          infoText='Se não for informado, valor será cadastrado para o tipo de atendimento'
          className='mb-3'
          isMulti
          highlight
        />

        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='tipoAtendimento'
            entity='tipoAtendimento'
            fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
            isDisabled={formik.isSubmitting || !!valorProfissional}
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='minimo'
            type='number'
            placeholder='Mínimo de atendimentos'
            label='Mínimo de atendimentos mensais'
            infoText='Deixe em branco para não ter mínimo'
            className='mb-3'
            highlight
          />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='valor' type='number' entity='valor' isRequired />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='valorRetorno' type='number' entity='valor_retorno' />
        </div>
      </div>
      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
